/**
 * Parametric timeline — core types.
 *
 * The design center: every modeling action is an immutable FeatureNode in an
 * ordered list (the timeline). Nodes reference earlier nodes by id, which
 * induces a DAG over the list. Editing a node produces a *new* node (and a new
 * Timeline), never a mutation — this is what makes undo/redo, hashing-based
 * regeneration caching, and structural sharing trivial.
 */

import type { Expr } from '../expr';

/** Branded id so feature ids don't mix with other strings. */
export type FeatureId = string & { readonly __brand: 'FeatureId' };

/**
 * Numeric feature params may hold an `{ $expr: "width/2" }` marker instead
 * of a number (parametric expressions against the document's parameter
 * table). The evaluator RESOLVES all markers before hashing/executing, so
 * kernels only ever see plain numbers — param types below stay `number`,
 * and UI/API write sites that store expressions cast through this type.
 */
export type NumberOrExpr = number | Expr;

/**
 * A reference from one feature's params to an upstream feature's output.
 * `path` optionally addresses a sub-entity of that output (a face, edge, or
 * sketch profile region), using stable topological names — e.g. "profile:0"
 * or "face:top". Stable naming is what keeps downstream features attached
 * when upstream geometry regenerates.
 */
export interface FeatureRef {
  readonly $feature: FeatureId;
  readonly path?: string;
}

export function ref(id: FeatureId, path?: string): FeatureRef {
  return path === undefined ? { $feature: id } : { $feature: id, path };
}

export function isFeatureRef(v: unknown): v is FeatureRef {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as FeatureRef).$feature === 'string'
  );
}

// ---------------------------------------------------------------------------
// Feature parameter payloads (one per feature type)
// ---------------------------------------------------------------------------

export type Vec2 = readonly [number, number];
export type Vec3 = readonly [number, number, number];

/** Where a sketch lives: a canonical datum plane, a planar face of a solid,
 * or a free plane captured from any face pick (origin + normal). */
export type SketchPlane =
  | { readonly kind: 'datum'; readonly plane: 'XY' | 'YZ' | 'XZ'; readonly offset: number }
  | { readonly kind: 'face'; readonly face: FeatureRef }
  | { readonly kind: 'free'; readonly origin: Vec3; readonly normal: Vec3 }
  /**
   * PARAMETRIC planar B-rep face: resolves against the body's CURRENT
   * geometry each regeneration — nearest planar face to `center` (the
   * face's centroid at pick time). The sketch rides that face through
   * edits, on ANY body (lofts, filleted parts…), not just extrude caps.
   */
  | { readonly kind: 'bface'; readonly body: FeatureRef; readonly center: Vec3; readonly normal: Vec3 };

/** 2D sketch entities. */
export type SketchEntity =
  | { readonly kind: 'line'; readonly a: Vec2; readonly b: Vec2 }
  | { readonly kind: 'rect'; readonly min: Vec2; readonly max: Vec2 }
  | { readonly kind: 'circle'; readonly center: Vec2; readonly radius: number }
  | { readonly kind: 'ellipse'; readonly center: Vec2; readonly rx: number; readonly ry: number }
  | {
      readonly kind: 'arc';
      readonly center: Vec2;
      readonly radius: number;
      readonly startAngle: number;
      readonly endAngle: number;
    }
  | {
      /** Fair curve THROUGH the given points (exact B-spline in the kernel). */
      readonly kind: 'spline';
      readonly points: readonly Vec2[];
      readonly closed?: boolean;
    }
  | {
      /**
       * Text: expands to glyph outline 'poly' loops on the main thread
       * (core/text) before any kernel sees it. `height` = em size in mm.
       */
      readonly kind: 'text';
      readonly at: Vec2;
      readonly height: number;
      readonly text: string;
    }
  | {
      /** Closed polygon loop (glyph outlines). Not drawn directly. */
      readonly kind: 'poly';
      readonly points: readonly Vec2[];
    };

/**
 * A named point on a sketch entity (by index into the entities list):
 * line/arc ends are 'a'/'b', 'mid' is a line/rect midpoint, circles and
 * arcs have 'center', rects have 'min'/'max' corners.
 */
export interface SketchPointRef {
  readonly entity: number;
  readonly point: 'a' | 'b' | 'mid' | 'center' | 'min' | 'max';
}

/** Geometric constraints solved numerically at evaluation time. */
export type SketchConstraint =
  | { readonly kind: 'coincident'; readonly a: SketchPointRef; readonly b: SketchPointRef }
  | { readonly kind: 'horizontal'; readonly entity: number }
  | { readonly kind: 'vertical'; readonly entity: number }
  | { readonly kind: 'parallel'; readonly a: number; readonly b: number }
  | { readonly kind: 'perpendicular'; readonly a: number; readonly b: number }
  | { readonly kind: 'equal'; readonly a: number; readonly b: number }
  | {
      readonly kind: 'distance';
      readonly a: SketchPointRef;
      readonly b: SketchPointRef;
      /** May be an expression against the document parameter table. */
      readonly value: NumberOrExpr;
    }
  | { readonly kind: 'radius'; readonly entity: number; readonly value: NumberOrExpr }
  | { readonly kind: 'fixed'; readonly point: SketchPointRef; readonly at: Vec2 }
  /** Line `a` tangent to circle/arc `b` (either order accepted). */
  | { readonly kind: 'tangent'; readonly a: number; readonly b: number }
  /** Two circles/arcs share a center. */
  | { readonly kind: 'concentric'; readonly a: number; readonly b: number }
  /** Points a and b mirror each other across line `about`. */
  | {
      readonly kind: 'symmetric';
      readonly a: SketchPointRef;
      readonly b: SketchPointRef;
      readonly about: number;
    };

export interface SketchParams {
  readonly plane: SketchPlane;
  readonly entities: readonly SketchEntity[];
  readonly constraints?: readonly SketchConstraint[];
}

export type BooleanOp = 'new-body' | 'join' | 'cut' | 'intersect';

export interface ExtrudeParams {
  /** Sketch profile region(s) to extrude. */
  readonly profile: FeatureRef;
  /** Signed distance; negative extrudes opposite the sketch normal (push/pull). */
  readonly distance: number;
  readonly op: BooleanOp;
  /** Optional second direction for symmetric / two-sided extrudes. */
  readonly distanceBack?: number;
  /**
   * Taper: scale of the FAR cap (the pushed end) about the profile centroid.
   * 1 = straight prism (default), 0 = apex (circle → cone), 0.5 = frustum,
   * >1 flares outward.
   */
  readonly endScale?: number;
}

export interface RevolveParams {
  readonly profile: FeatureRef;
  readonly axisOrigin: Vec2;
  readonly axisDirection: Vec2;
  /** Radians; 2π for a full revolve. */
  readonly angle: number;
  readonly op: BooleanOp;
}

export interface SweepParams {
  readonly profile: FeatureRef;
  readonly path: FeatureRef;
  readonly op: BooleanOp;
}

/**
 * Loft: a solid through 2+ closed sketch profiles in order (hull stations).
 * `ruled` = straight sections between profiles; default is smooth. An
 * optional open-curve `spine` sketch guides the surface between sections
 * (keel/sheer line) via a pipe-shell sweep instead of a plain loft.
 */
export interface LoftParams {
  readonly profiles: readonly FeatureRef[];
  readonly ruled?: boolean;
  readonly op: BooleanOp;
  readonly spine?: FeatureRef;
}

/** Hollow a body, opening the top/bottom face (B-rep kernel). */
export interface ShellParams {
  readonly target: FeatureRef;
  readonly thickness: number;
  readonly open: 'top' | 'bottom' | 'none';
}

/**
 * Round edges of a body (B-rep kernel). `edges` lists PICKED edge
 * identities as curve midpoints in world space — each matches the nearest
 * edge of the regenerated body, so picks follow parametric edits. Absent =
 * every edge.
 */
export interface FilletParams {
  readonly target: FeatureRef;
  readonly radius: number;
  readonly edges?: readonly Vec3[];
}

/** Chamfer edges of a body (B-rep kernel). Same edge picking as fillet. */
export interface ChamferParams {
  readonly target: FeatureRef;
  readonly distance: number;
  readonly edges?: readonly Vec3[];
}

/**
 * Axis-aligned scale of an existing body (consumes it).
 * Per axis k: p'ₖ = centerₖ + (pₖ − centerₖ)·factorsₖ — so `center` is three
 * independent per-axis anchor coordinates, not necessarily one point (they
 * diverge when drags use different anchors, e.g. opposite-face handles).
 */
export interface ScaleParams {
  readonly target: FeatureRef;
  /** Per-axis factors. Preferred form. */
  readonly factors?: Vec3;
  /** Legacy uniform factor (older documents); used when `factors` absent. */
  readonly factor?: number;
  readonly center: Vec3;
}

/**
 * Group: bundle several bodies into ONE (consumes them). Geometry is NOT
 * boolean-merged — shells stay distinct, exactly like a SketchUp group —
 * but downstream features (move, scale, mirror, booleans, instances) see a
 * single body. Ungroup = delete the feature; the originals reappear.
 */
export interface GroupParams {
  readonly targets: readonly FeatureRef[];
}

/**
 * Component instance: place a transformed COPY of a source body without
 * consuming it. All instances track the source parametrically — edit
 * anything upstream and every instance regenerates. `copies` arrays further
 * placements at multiples of the transform (like TransformParams).
 */
export interface InstanceParams {
  readonly source: FeatureRef;
  readonly translation: Vec3;
  readonly rotationAxis: 'X' | 'Y' | 'Z';
  readonly rotationAngle: number;
  readonly rotationCenter: Vec3;
  readonly copies: number;
}

/**
 * Move/rotate a body (consumes it), optionally arraying copies.
 * copies = 0: the body moves. copies = N: the original stays and N copies
 * land at i×translation / i×rotation (SketchUp's "5x" move-copy array —
 * linear for pure moves, circular for pure rotations).
 */
export interface TransformParams {
  readonly target: FeatureRef;
  readonly translation: Vec3;
  readonly rotationAxis: 'X' | 'Y' | 'Z';
  /** Radians, about `rotationCenter` (applied before the translation). */
  readonly rotationAngle: number;
  readonly rotationCenter: Vec3;
  readonly copies: number;
}

/**
 * Mirror a body across a world-aligned plane. `keepOriginal` makes it a
 * symmetric copy (both halves in one body); off = flip in place.
 */
export interface MirrorParams {
  readonly target: FeatureRef;
  /** Mirror plane: YZ reflects X, XZ reflects Y, XY reflects Z. */
  readonly plane: 'YZ' | 'XZ' | 'XY';
  readonly offset: number;
  readonly keepOriginal: boolean;
}

/**
 * Assembly component: another PROJECT inserted as one body. The source
 * project's timeline is re-evaluated during regeneration (its content hash
 * feeds this feature's identity), so editing the part project updates every
 * assembly that uses it. Placed by rotating about the world origin, then
 * translating — position with the move gizmo/snaps like any body.
 */
export interface PartParams {
  readonly projectId: string;
  /** Display name of the source project (informational; id is the link). */
  readonly source: string;
  readonly position: Vec3;
  /** Euler XYZ, radians. */
  readonly rotationEuler: Vec3;
}

/**
 * Coil: a round-wire helix (springs, wound features). The profile circle is
 * auto-positioned at the helix start, perpendicular to the curve — no
 * profile sketch needed. All numerics take expressions.
 */
export interface CoilParams {
  readonly axisOrigin: Vec3;
  readonly axis: 'X' | 'Y' | 'Z';
  readonly radius: NumberOrExpr;
  /** Height advanced per full turn (mm). */
  readonly pitch: NumberOrExpr;
  readonly turns: NumberOrExpr;
  readonly wireDiameter: NumberOrExpr;
  readonly leftHanded?: boolean;
  readonly op: BooleanOp;
}

/**
 * Primitive: an analytic solid straight from the kernel (no sketch).
 * `at` is the box CENTER / axis base point / sphere or torus center;
 * round shapes stand along `axis`. Size fields by shape:
 * box dx/dy/dz · cylinder radius/height · sphere radius ·
 * cone radius/radius2(top, 0=apex)/height · torus majorRadius/minorRadius.
 */
export interface PrimitiveParams {
  readonly shape: 'box' | 'cylinder' | 'sphere' | 'cone' | 'torus';
  readonly at: Vec3;
  readonly axis: 'X' | 'Y' | 'Z';
  readonly dx?: NumberOrExpr;
  readonly dy?: NumberOrExpr;
  readonly dz?: NumberOrExpr;
  readonly radius?: NumberOrExpr;
  readonly radius2?: NumberOrExpr;
  readonly height?: NumberOrExpr;
  readonly majorRadius?: NumberOrExpr;
  readonly minorRadius?: NumberOrExpr;
  readonly op: BooleanOp;
}

/**
 * Hole wizard: drill one or more holes into a body, each at a picked point
 * on a face, along the face normal (into the material). Plain, counterbore,
 * or countersink; depth in mm or 'through'. Positions are face points
 * captured at pick time — through-holes track thickness changes; blind
 * depths are measured from the picked surface.
 */
export interface HoleParams {
  readonly target: FeatureRef;
  readonly holes: readonly { readonly point: Vec3; readonly normal: Vec3 }[];
  readonly diameter: NumberOrExpr;
  readonly depth: NumberOrExpr | 'through';
  readonly style: 'plain' | 'counterbore' | 'countersink';
  /** Counterbore: flat-bottom enlargement. */
  readonly cbDiameter?: NumberOrExpr;
  readonly cbDepth?: NumberOrExpr;
  /** Countersink: included angle in degrees (82/90/120), default 90. */
  readonly csAngle?: number;
  readonly csDiameter?: NumberOrExpr;
}

/**
 * Pattern: array a body linearly or around an axis, parametric count.
 * Lowers to the transform kernel op at evaluation (consumes the source;
 * original counts as instance #0). Circular spreads count instances over
 * `totalAngleDeg` (default full circle).
 */
export interface PatternParams {
  readonly target: FeatureRef;
  readonly kind: 'linear' | 'circular';
  readonly count: NumberOrExpr;
  /** Linear: step between instances. */
  readonly step?: Vec3;
  /** Circular: rotation axis + center. */
  readonly axis?: 'X' | 'Y' | 'Z';
  readonly center?: Vec3;
  readonly totalAngleDeg?: NumberOrExpr;
}

/**
 * Named anchor points on a body's bounding box:
 * center = bbox center · bottom/top = bbox center at min/max Z ·
 * min/max = bbox corners.
 */
export type MateAnchor = 'center' | 'bottom' | 'top' | 'min' | 'max';

/**
 * Assembly mate: move `target` relative to `to`. Parametric — geometry
 * recomputes from the regenerated bodies each pass. Consumes `target`.
 *
 * kind 'anchor' (default): translate so target's bbox anchor lands on
 * to's anchor + offset.
 * kind 'concentric': rotate + translate so the cylindrical face picked on
 * target (identity = `faceTarget` centroid, nearest-match) becomes coaxial
 * with the one picked on `to` — stud into hole. Position along the shared
 * axis is preserved; stack with an anchor mate to set it.
 */
export interface MateParams {
  readonly target: FeatureRef;
  readonly to: FeatureRef;
  readonly kind?: 'anchor' | 'concentric' | 'flush' | 'parallel' | 'distance' | 'angle';
  readonly anchorTarget: MateAnchor;
  readonly anchorTo: MateAnchor;
  readonly offset: Vec3;
  /**
   * Concentric: pick-time centroids of the two cylindrical faces.
   * Planar mates (flush/parallel/distance/angle): centroids of the two
   * PLANAR faces to bring together — target's face turns to oppose to's.
   */
  readonly faceTarget?: Vec3;
  readonly faceTo?: Vec3;
  /** distance mate: gap (mm) held between the two faces along the normal. */
  readonly gap?: NumberOrExpr;
  /** angle mate: angle (deg) between the two face normals. */
  readonly angleDeg?: NumberOrExpr;
}

/**
 * Direct-modeling press/pull: prism the planar face nearest `facePoint`
 * along its outward normal by `offset` (+ adds material, − removes).
 */
export interface PressPullParams {
  readonly target: FeatureRef;
  readonly facePoint: Vec3;
  readonly offset: NumberOrExpr;
}

/**
 * Real ISO metric thread: a 60° truncated-V ridge swept along a helix.
 * op join/new-body = external stud (ridges fused on a minor-⌀ core);
 * op cut = internal tap (cut the ridges from a drilled hole's wall).
 */
export interface ThreadParams {
  readonly axisOrigin: Vec3;
  readonly axis: 'X' | 'Y' | 'Z';
  /** Nominal (major) diameter, e.g. 10 for M10. */
  readonly diameter: NumberOrExpr;
  readonly pitch: NumberOrExpr;
  readonly length: NumberOrExpr;
  readonly leftHanded?: boolean;
  readonly op: BooleanOp;
}

/**
 * Mold draft: tilt faces by `angleDeg` about a neutral (parting) plane ⊥ the
 * pull `axis` at `neutralOffset`. `faces` = pick-time centroids
 * (nearest-match, like fillet edges); omitted = all walls parallel to pull.
 */
export interface DraftParams {
  readonly target: FeatureRef;
  readonly angleDeg: NumberOrExpr;
  readonly axis: 'X' | 'Y' | 'Z';
  readonly neutralOffset: NumberOrExpr;
  readonly faces?: readonly Vec3[];
}

/**
 * EXACT B-rep import (STEP/IGES): the file text lives content-addressed in
 * the assets store; the kernel parses it into a true solid, so the imported
 * part supports fillets, booleans, and mass properties like native bodies.
 */
export interface ImportStepParams {
  readonly assetHash: string;
  readonly name: string;
  readonly format: 'step' | 'iges';
}

/** Non-parametric reference mesh (asset-browser imports, STL/OBJ/GLTF). */
export interface ImportMeshParams {
  /** Content-addressed key into the assets table in IndexedDB. */
  readonly assetHash: string;
  readonly name: string;
  readonly transform: {
    readonly position: Vec3;
    readonly rotationEuler: Vec3;
    readonly scale: Vec3;
  };
}

export interface FeatureParamsByType {
  sketch: SketchParams;
  extrude: ExtrudeParams;
  revolve: RevolveParams;
  sweep: SweepParams;
  loft: LoftParams;
  shell: ShellParams;
  fillet: FilletParams;
  chamfer: ChamferParams;
  scale: ScaleParams;
  mirror: MirrorParams;
  transform: TransformParams;
  group: GroupParams;
  instance: InstanceParams;
  part: PartParams;
  mate: MateParams;
  hole: HoleParams;
  pattern: PatternParams;
  coil: CoilParams;
  primitive: PrimitiveParams;
  'import-mesh': ImportMeshParams;
  'import-step': ImportStepParams;
  draft: DraftParams;
  thread: ThreadParams;
  'press-pull': PressPullParams;
}

export type FeatureType = keyof FeatureParamsByType;
export type FeatureParams = FeatureParamsByType[FeatureType];

// ---------------------------------------------------------------------------
// The node itself
// ---------------------------------------------------------------------------

export interface FeatureNode<T extends FeatureType = FeatureType> {
  readonly id: FeatureId;
  readonly type: T;
  /** User-visible label, e.g. "Extrude 3". */
  readonly name: string;
  readonly params: FeatureParamsByType[T];
  /**
   * Upstream feature ids this node consumes, derived from FeatureRefs in
   * params at construction time. Invariant: every dependency appears
   * *earlier* in the timeline (the list order is a topological order).
   */
  readonly dependencies: readonly FeatureId[];
  /** Suppressed features are kept in the DAG but skipped during evaluation. */
  readonly suppressed: boolean;
  /**
   * Material library id (see core/materials.ts) and optional #rrggbb color
   * override. Like `name`, these live OUTSIDE the evaluator's content hash:
   * changing a material re-renders and re-weighs, never re-executes kernels.
   * Bodies without one inherit through the consumed-ancestor chain.
   */
  readonly material?: string;
  readonly materialColor?: string;
}
